import { formatDate, formatMoney } from '@betterment/core';
import { HStack, Text, VStack } from '@betterment/samba-react';
import { css } from '@betterment/samba-styled-system/css';
import { useCursor } from '@betterment/shared-components';

import { UserBalancePerformanceQuery } from './graphql/userPerformanceBalance.generated';

import { DateRangeOption, PerformanceHistoryItem } from '../../../../graphql/schema';

const getEarnedLabel = (dateRangeOption: DateRangeOption): string => {
  const labels: Record<DateRangeOption, string> = {
    [DateRangeOption.AllTime]: 'Earned all time',
    [DateRangeOption.Custom]: 'Earned',
    [DateRangeOption.PastFiveYears]: 'Earned past five years',
    [DateRangeOption.PastMonth]: 'Earned past month',
    [DateRangeOption.PastThreeMonths]: 'Earned past three months',
    [DateRangeOption.PastThreeYears]: 'Earned past three years',
    [DateRangeOption.PastTwoYears]: 'Earned past two years',
    [DateRangeOption.PastWeek]: 'Earned past week',
    [DateRangeOption.PastYear]: 'Earned past year',
    [DateRangeOption.Today]: 'Earned today',
    [DateRangeOption.YearToDate]: 'Earned YTD',
  };
  return labels[dateRangeOption];
};

export const BalanceChartData = ({
  totals,
  dateRangeOption,
  loading = true,
}: {
  totals?: UserBalancePerformanceQuery['performance']['totals'];
  dateRangeOption: DateRangeOption;
  loading: boolean;
}) => {
  const { data } = useCursor<PerformanceHistoryItem>();

  if (loading || (!data && !totals)) return null;

  // Cursor data (when hovering over chart)
  const balanceLabel = data ? formatDate(data.date) : 'Balance';
  const balanceValue = data ? data.balance : totals!.endingBalance;
  const netDeposits = data ? `${formatMoney(data.invested)} net deposits` : null;

  const earnedLabel = data ? `Earned as of ${formatDate(data.date)}` : getEarnedLabel(dateRangeOption);
  const earnedValue = data?.earned || totals?.earned;
  const earnedColor = earnedValue && earnedValue.cents > 0 ? 'text.success' : 'text';
  const earnedFormat = earnedValue && earnedValue.cents > 0 ? 'credit' : 'debit';

  const columnStyle = css({ width: '50%' });

  return (
    <HStack>
      <VStack className={columnStyle}>
        <Text variant="text50">{balanceLabel}</Text>
        <Text variant="text300">{formatMoney(balanceValue)}</Text>
        <Text variant="text50">{netDeposits || '\u00A0'}</Text>
      </VStack>
      <VStack className={columnStyle}>
        <Text variant="text50">{earnedLabel}</Text>
        <Text variant="text300" color={earnedColor}>
          {earnedValue && formatMoney(earnedValue, earnedFormat)}
        </Text>
      </VStack>
    </HStack>
  );
};
